(ns shared.core.amelia-light.components.date-picker
  (:require [clojure.string :as s]
            [shared.core.components.date-picker :as date-picker]
            [shared.core.amelia-light.components.button :as button]
            [shared.core.amelia-light.components.icon :as icon]
            [shared.core.style :as style]
            [shared.core.util :as util]
            [shared.core.reactive :as re]))

(defn theme-mode-class
  "Class name that reflects the active dark/light theme, applied alongside
  `amelia-light` so the load-time CSS below can be scoped per mode."
  [theme]
  (if (= theme :dark-mode) "dark-mode" "light-mode"))

(defn- date-picker-rules
  "Datepicker CSS rules resolved for a specific `theme`. `alc-color` is normally
  reactive, but this stylesheet is injected once at load time, so we pass the
  theme explicitly and emit a rule set per mode (see `scope-rules`)."
  [theme]
  (let [c #(style/alc-color % theme)]
    [[".amelia-light .react-datepicker-wrapper" {:width "100%"}]
     [".amelia-light .react-datepicker__header, .amelia-light .react-datepicker__header:not(.react-datepicker__header--has-time-select)"
      {:background-color "transparent"
       :padding-top "0px"
       :border-top-left-radius "12px"
       :border-top-right-radius "12px"
       :border-bottom :none}]
     [".amelia-light .react-datepicker__month"
      {:margin 0
       :padding "0 var(--2_5, 10px) var(--3, 12px)"}]
     [".amelia-light .react-datepicker__day-names"
      {:display :flex
       :flex-direction :row
       :padding "var(--1_5, 6px) var(--2_5, 10px) 0"}]
     [".amelia-light .react-datepicker__week"
      {:display :flex
       :flex-direction :row
       :padding "var(--1_5, 6px) 0 0"}]
     [".amelia-light .react-datepicker__day-name, .amelia-light .react-datepicker__day, .amelia-light .react-datepicker__day--in-range:hover"
      {:display :flex
       :justify-content :center
       :align-items :center
       :padding "6px"
       :margin 0
       :font-family style/roboto-font-family
       :font-weight style/font-regular-weight
       :font-size "14px"
       :line-height 1.43
       :color (c "Text/secondary")
       :width "var(--10, 40px)"
       :height "var(--10, 40px)"}]
     [".amelia-light .react-datepicker__day--outside-month, .amelia-light .react-datepicker__day.react-datepicker__day--disabled"
      {:color (c "Text/secondary_disabled")
       :opacity 1}]
     [(str ".amelia-light .react-datepicker__month-text--keyboard-selected, .amelia-light .react-datepicker__quarter-text--keyboard-selected, .amelia-light .react-datepicker__year-text--keyboard-selected, .amelia-light .react-datepicker__year-text--keyboard-selected:hover,"
           ".amelia-light .react-datepicker__day--selected, .amelia-light .react-datepicker__day--in-selecting-range, .amelia-light .react-datepicker__day--in-range, .amelia-light .react-datepicker__month-text--selected, .amelia-light .react-datepicker__month-text--in-selecting-range, .amelia-light .react-datepicker__month-text--in-range, .amelia-light .react-datepicker__quarter-text--selected, .amelia-light .react-datepicker__quarter-text--in-selecting-range, .amelia-light .react-datepicker__quarter-text--in-range, .amelia-light .react-datepicker__year-text--selected, .amelia-light .react-datepicker__year-text--in-selecting-range, .amelia-light .react-datepicker__year-text--in-range, .amelia-light .react-datepicker__year-text--in-range:hover,"
           ".amelia-light .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item--selected,"
           ".amelia-light .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item--selected:hover,"
           ".amelia-light .react-datepicker__day--selected:hover, .amelia-light .react-datepicker__day--in-selecting-range:hover, .amelia-light .react-datepicker__day--in-range:hover, .amelia-light .react-datepicker__month-text--selected:hover, .amelia-light .react-datepicker__month-text--in-selecting-range:hover, .amelia-light .react-datepicker__month-text--in-range:hover, .amelia-light .react-datepicker__quarter-text--selected:hover, .amelia-light .react-datepicker__quarter-text--in-selecting-range:hover, .amelia-light .react-datepicker__quarter-text--in-range:hover, .amelia-light .react-datepicker__year-text--selected:hover, .amelia-light .react-datepicker__year-text--in-selecting-range:hover, .amelia-light .react-datepicker__year-text--in-range:hover")
      {:background-color "transparent"
       :border-radius "0px"}]
     [".amelia-light .react-datepicker__day"
      {:color (c "Text/primary")
       :position :relative
       :transform-style "preserve-3d"}]
     [(str ".amelia-light .react-datepicker__day--in-range:not(.react-datepicker__day--range-start):not(.react-datepicker__day--range-end)::before,"
           ".amelia-light .react-datepicker__day--in-range:not(.react-datepicker__day--range-start):not(.react-datepicker__day--range-end)::after,",
           ".amelia-light .react-datepicker__day--in-selecting-range:not(.react-datepicker__day--keyboard-selected)::before, .amelia-light .react-datepicker__day--in-selecting-range:not(:hover)::after,",
           ".amelia-light .react-datepicker__day--range-end:not(.react-datepicker__day--range-start)::before, .amelia-light .react-datepicker__day--range-start:not(.react-datepicker__day--range-end)::after")
      {:content "''"
       :height "100%"
       :width "50%"
       :background-color (c "Background/active_secondary_hex")
       :position :absolute
       :transform "translateZ(-1px)"
       :top 0}]
     [(str ".amelia-light .react-datepicker__day--range-start:not(.react-datepicker__day--range-end)::after, .amelia-light .react-datepicker__day--range-end:not(.react-datepicker__day--range-start)::before")
      {:width "50%"}]
     [(str ".amelia-light .react-datepicker__day--in-range:not(.react-datepicker__day--range-start):not(.react-datepicker__day--range-end)::before,"
           ".amelia-light .react-datepicker__day--range-end:not(.react-datepicker__day--range-start)::before, .amelia-light .react-datepicker__day--in-selecting-range:not(.react-datepicker__day--keyboard-selected)::before")
      {:left 0}]
     [(str ".amelia-light .react-datepicker__day--in-range:not(.react-datepicker__day--range-start):not(.react-datepicker__day--range-end)::after,"
           ".amelia-light .react-datepicker__day--range-start:not(.react-datepicker__day--range-end)::after, .amelia-light .react-datepicker__day--in-selecting-range:not(:hover)::after")
      {:border-radius 0
       :left "50%"}]
     [".amelia-light .react-datepicker__day:not(.react-datepicker__day--disabled):hover, .amelia-light .react-datepicker__day--today:not(.react-datepicker__day--selected):hover"
      {:background-color (c "Background/active_secondary_hex")
       :color (c "Text/primary")
       :border-radius "50%"
       :transform-style "preserve-3d"}]
     [(str ".amelia-light .react-datepicker__day--keyboard-selected:hover")
      {:background-color (c "Background/active_secondary_hex")
       :color (c "Text/primary")
       :border-radius "50%"}]
     [(str ".amelia-light .react-datepicker__day--in-selecting-range:not(.react-datepicker__day--keyboard-selected)::before")
      {:background-color (c "Background/active_secondary_hex")
       :transform "translateZ(-1px)"}]
     [(str ".amelia-light .react-datepicker__day--keyboard-selected")
      {:background-color "transparent"
       :color (c "Text/primary")
       :border-radius "50%"}]
     [(str ".amelia-light .react-datepicker__day--range-start, .amelia-light .react-datepicker__day--range-end,"
           ".amelia-light .react-datepicker__day--range-start:not(.react-datepicker__day--selected):hover, .amelia-light .react-datepicker__day--range-end:not(.react-datepicker__day--selected):hover,"
           ".amelia-light .react-datepicker__day--range-start:hover, .amelia-light .react-datepicker__day--range-end:hover,"
           ".amelia-light .react-datepicker__day--selecting-range-start, .amelia-light .react-datepicker__day--selecting-range-start:hover,"
           ".amelia-light .react-datepicker__day--today.react-datepicker__day--range-end:not(.react-datepicker__day--selected)")
      {:background-color (c "Common/active")
       :color (c "Common/White")
       :transform-style "preserve-3d"
       :border-radius "50%"}]
     [".amelia-light .react-datepicker__day--selecting-range-start.react-datepicker__day--selecting-range-end::after"
      {:width "100%"}]
     [".amelia-light .react-datepicker__day--today:not(.react-datepicker__day--selected)"
      {:color (c "Text/primary")}]
     [".amelia-light .react-datepicker__day--today::after"
      {:content "''"
       :width "3px"
       :height "3px"
       :border-radius "50%"
       :background-color (c "Background/active_secondary")
       :position :absolute
       :left "50%"
       :bottom "5px"
       :transform "translateX(-50%)"}]
     [".amelia-light .react-datepicker__aria-live"
      {:display "none"}]
     [".amelia-light .react-datepicker__day--today.react-datepicker__day--range-end::after"
      {:background-color "transparent"}]
     [".react-datepicker__day--today:not(.react-datepicker__day--selected):not(:hover)"
      {:background-color "transparent"}]]))

(defn- scope-rules
  "Scope each rule's selectors under `.amelia-light.<mode>` so only the rules
  matching the active theme's mode class take effect."
  [theme rules]
  (let [mode (theme-mode-class theme)]
    (map (fn [rule]
           (update rule 0 #(s/replace % ".amelia-light" (str ".amelia-light." mode))))
         rules)))

;; The stylesheet is injected once at load time, so emit both light and dark
;; variants (scoped by mode class) and let the reactive class on the element
;; below decide which set applies.
(apply style/add-css
       (concat (scope-rules :light-mode (date-picker-rules :light-mode))
               (scope-rules :dark-mode (date-picker-rules :dark-mode))))

(defn date-range-picker
  [{:keys [val-subscribe form-path form-name extra-on-change on-cancel on-apply]}]
  (let [val- (if val-subscribe
               (re/subscribe val-subscribe)
               (when (and form-path form-name)
                 (re/subscribe [:form/val- form-path form-name])))
        start-end* (re/ratom [nil nil])]
    (fn [{:keys [inline? class-name value] :or {inline? true} :as args}]
      (let [[start end] (or value (if val- @val- @start-end*))]
        [date-picker/date-picker
         (merge {:start-date start
                 :end-date end
                 :selects-range true
                 :inline inline?
                 :class-name (s/trim (str "amelia-light " (theme-mode-class @(re/subscribe [:alc/theme-])) " " class-name))
                 :on-change (fn [start-end]
                              (if (and form-path form-name)
                                (re/dispatch [:form/change form-path form-name start-end])
                                (reset! start-end* start-end))
                              (when extra-on-change (extra-on-change start-end)))
                 :render-custom-header
                 (fn [props]
                   (let [{:keys [date
                                 decrease-month
                                 increase-month
                                 prev-month-button-disabled
                                 next-month-button-disabled]} (util/->kebab-case-keywords (js->clj props :keywordize-keys true))
                         month (.toLocaleDateString date "en-US" #js {:month "short"})
                         year (.getFullYear date)]
                     (re/as-element [:div {:style {:display :flex
                                                   :justify-content :space-between
                                                   :align-items :center
                                                   :padding "9px 8px"
                                                   :border-bottom (str "1px solid " (style/alc-color "Border/divider_secondary"))}}
                                     [button/up-ghost-button {:icon "md"
                                                              :on-click (when (not prev-month-button-disabled) decrease-month)
                                                              :disabled prev-month-button-disabled}
                                      [icon/up-icon {:icon-type :ChevronLeftIcon}]]
                                     [:span {:style {:font-family style/roboto-font-family
                                                     :font-weight 400
                                                     :color (style/alc-color "Text/primary")
                                                     :font-size 14
                                                     :line-height 1.43}}
                                      (str month " " year)]
                                     [button/up-ghost-button {:icon "md"
                                                              :on-click (when (not next-month-button-disabled) increase-month)
                                                              :disabled next-month-button-disabled}
                                      [icon/up-icon {:icon-type :ChevronRightIcon}]]])))
                 :calendar-container
                 (fn [props]
                   (let [children (.-children props)]
                     (re/as-element
                       [:div {:style {:display :flex
                                      :flex-direction :column
                                      :background-color (style/alc-color "Background/primary")
                                      :box-shadow "0 2px 4px 0 rgba(0, 0, 0, 0.03), 0 6px 18px 0 rgba(0, 0, 0, 0.08)"
                                      :border (str "1px solid " (style/alc-color "Border/divider_secondary"))
                                      :border-radius "12px"}}
                        children
                        (when (or on-cancel on-apply)
                          [:div {:style {:display :flex
                                         :justify-content (if on-cancel :space-between :flex-end)
                                         :align-items :center
                                         :padding "9px 12px"
                                         :border-top (str "1px solid " (style/alc-color "Border/divider_secondary"))}}
                           (when on-cancel
                             [button/up-button {:on-click on-cancel}
                              "Reset"])
                           (when on-apply
                             [button/up-call-to-action-button {:disabled (not (and start end))
                                                               :on-click (when (and start end) #(on-apply [start end]))}
                              "Apply"])])])))}
                (dissoc args :val-subscribe :form-path :form-name :extra-on-change :on-cancel :on-apply))]))))

